import { memo, useEffect, useState } from 'react';

import { CalendarIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import {
  Tooltip as ApsaraTooltip,
  Flex,
  IconButton,
  Table,
  Text
} from '@raystack/apsara';
import * as _ from 'lodash';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import * as Skeleton from '@src/components/PixxelUIKit/Skeleton';
import {
  FOREST_AREA_INFO,
  FOREST_BIO_GRAPH_NAME
} from '@src/utils/constants/MODE_INFERENCE_DEFAULTS';
import { INPUT_FORMATS } from '@src/utils/constants/WORKFLOW_CONSTANTS';
import { fetchTabData } from '@src/utils/helpers/WorkflowHelpers';
import { getCogURLForOutput } from '@src/utils/workFlowUtils';

import styles from './styles.module.css';

export const getForestBiomassOutputTilerUrl = (outputs, format) => {
  const raster = outputs.find(i => i.format === INPUT_FORMATS.FormatRaster);

  if (!raster) return '';

  return getCogURLForOutput(raster, format);
};

const ForestBiomasGraph = ({ data }) => {
  return (
    <>
      <BarChart
        width={370}
        height={350}
        data={data}
        margin={{
          top: 0,
          bottom: 16,
          right: 0,
          left: -10
        }}
      >
        <XAxis
          dataKey='Range'
          fontSize={11}
          label={{
            value: 'Range (Mg/ha)',
            style: { textAnchor: 'middle', fontSize: '11px' },
            angle: 0,
            position: 'insideBottom',
            offset: -10
          }}
        />
        <YAxis
          fontSize={12}
          dataKey='Area Percentage (%)'
          label={{
            value: 'Area (%)',
            style: { textAnchor: 'middle', fontSize: '11px' },
            angle: -90,
            position: 'left',
            offset: -18
          }}
        />
        <CartesianGrid
          strokeDasharray='3 3'
          stroke='var(--rs-color-foreground-base-secondary)'
          vertical={false}
          opacity={0.4}
        />
        <Tooltip
          itemStyle={{
            color: 'var(--rs-color-foreground-base-primary)',
            fontSize: '11px'
          }}
          labelStyle={{
            color: 'var(--rs-color-background-accent-emphasis-hover)',
            fontSize: '14px'
          }}
          contentStyle={{
            backgroundColor: 'var(--rs-color-background-base-primary)',
            borderRadius: 'var(--rs-radius-2)',
            border: `1px solid var(--rs-color-border-base-secondary)`,
            padding: '8px',
            outline: 'none'
          }}
        />
        <Bar dataKey='Area Percentage (%)'>
          {data.map((bar, index) => (
            <Cell key={index} fill={bar?.color} />
          ))}
        </Bar>
      </BarChart>
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.Head></Table.Head>
            <Table.Head>Range (Mg/ha)</Table.Head>
            <Table.Head>Area (%)</Table.Head>
            <Table.Head>
              Area (km<sup>2</sup>){' '}
            </Table.Head>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.map((item, index) => (
            <Table.Row key={index}>
              <Table.Cell>
                <div
                  className={styles.filledBox}
                  style={{
                    backgroundColor: `${item?.color}`
                  }}
                />
              </Table.Cell>
              <Table.Cell>{item['Range']}</Table.Cell>
              <Table.Cell>{item['Area Percentage (%)']} %</Table.Cell>
              <Table.Cell>{item['area']}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </>
  );
};

export const ForestBiomassCommon = memo(
  ({ inputs, outputs, activeAoi = null }) => {
    const [tabData, setTabData] = useState({});
    const [activeTabData, setActiveTabData] = useState([]);
    const [loader, setLoader] = useState(false);
    const inputYear = inputs.find(i => i.format === INPUT_FORMATS.FormatDate);

    useEffect(() => {
      if (outputs) {
        // TODO: remove this after MLOPS team updates output format to correct value
        const csvOutput = [
          {
            ...Object.keys(outputs)
              .map(output => outputs[output])
              .find(i => i.name === 'fagb_stats_csv_path'),
            format: INPUT_FORMATS.FormatTypeTabular
          }
        ];
        fetchTabData(
          csvOutput,
          setTabData,
          value => setLoader(value),
          activeAoi
        );
      }
    }, [outputs, activeAoi]);

    //   form graph data for recharts
    useEffect(() => {
      if (!_.isEmpty(tabData)) {
        const graphList = tabData[FOREST_BIO_GRAPH_NAME];
        const graphData = [
          ...graphList.map(item => ({
            Range: `${item.min}-${item.max}`,
            'Area Percentage (%)': parseFloat(item.area_percentage),
            area: item.area,
            color: item.hex_colors
          }))
        ];
        setActiveTabData(graphData);
      }
    }, [tabData]);

    return (
      <>
        <Flex
          direction='column'
          align='start'
          gap='small'
          style={{ width: '100%' }}
        >
          <Text variant='primary' size={4} weight={500}>
            Timeframe
          </Text>
          <Flex gap='small' align='center'>
            <CalendarIcon color='var(--rs-color-foreground-base-secondary)' />
            <Text variant='primary' weight={400} size={4}>
              {inputYear?.value ?? ''}
            </Text>
          </Flex>
        </Flex>
        <Flex
          direction='column'
          gap='medium'
          align='start'
          style={{ width: '100%' }}
        >
          <Flex align='center' gap='small'>
            <Text size={4} weight={500} variant='primary'>
              Area Coverage
            </Text>
            <ApsaraTooltip message={FOREST_AREA_INFO} side='top'>
              <IconButton size={3} data-test-id='forest-biomass-info-btn'>
                <InfoCircledIcon />
              </IconButton>
            </ApsaraTooltip>
          </Flex>

          {loader ? (
            <Skeleton.TileList />
          ) : (
            <>
              {activeTabData && activeTabData.length > 0 && (
                <ForestBiomasGraph data={activeTabData.slice(0, -1)} />
              )}
            </>
          )}
        </Flex>
      </>
    );
  }
);

ForestBiomassCommon.displayName = 'ForestBiomassCommon';
